import React, { useState, useEffect } from 'react';
import { ArrowUpIcon, ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { adminApiService as apiService } from '../../services/adminApiService';
import { ApiError } from '../../types/errorTypes';

interface EligibleReport {
  id: number;
  name: string;
  type: string;
  current_version: string;
  target_version: string;
  created_at: string;
  last_edited_at: string | null;
  compatible: boolean;
  compatibility_message: string;
  warnings: string[];
}

interface BulkOperation {
  id: number;
  status: string;
  progress: {
    percentage: number;
    total_items: number;
    processed_items: number;
    successful_items: number;
    failed_items: number;
  };
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  can_cancel: boolean;
  results?: Record<string, {
    success: boolean;
    changes?: Array<{
      job_number: string;
      customer: string;
      commission_delta: number;
      net_profit_delta: number;
      tier_change: { from: string; to: string; } | null;
    }>;
    message?: string;
    error?: string;
  }>;
}

const RuleUpgradeManager: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eligibleReports, setEligibleReports] = useState<EligibleReport[]>([]);
  const [selectedReports, setSelectedReports] = useState<Set<number>>(new Set());
  const [currentRuleVersion, setCurrentRuleVersion] = useState<string>('');
  const [previewOnly, setPreviewOnly] = useState(true);
  const [activeOperation, setActiveOperation] = useState<BulkOperation | null>(null);
  const [operationHistory, setOperationHistory] = useState<BulkOperation[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchEligibleReports();
    fetchOperationHistory();
    
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEligibleReports = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.get('/v2/admin/bulk-operations/eligible-reports');
      setEligibleReports(response.data.data.eligible_reports);
      setCurrentRuleVersion(response.data.data.current_rule_version);
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to fetch eligible reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchOperationHistory = async () => {
    try {
      const response = await apiService.get('/v2/admin/bulk-operations/history?operation_type=rule_upgrade');
      setOperationHistory(response.data.data);
    } catch (err) {
      console.error('Failed to fetch operation history:', err);
    }
  };

  const handleSelectAll = () => {
    if (selectedReports.size === eligibleReports.filter(r => r.compatible).length) {
      setSelectedReports(new Set());
    } else {
      const allCompatible = eligibleReports
        .filter(r => r.compatible)
        .map(r => r.id);
      setSelectedReports(new Set(allCompatible));
    }
  };

  const handleSelectReport = (reportId: number) => {
    const newSelected = new Set(selectedReports);
    if (newSelected.has(reportId)) {
      newSelected.delete(reportId);
    } else {
      newSelected.add(reportId);
    }
    setSelectedReports(newSelected);
  };

  const startRuleUpgrade = async () => {
    if (selectedReports.size === 0) {
      setError('Please select at least one report to upgrade');
      return;
    }

    try {
      const response = await apiService.post('/v2/admin/bulk-operations/upgrade-rules', {
        report_ids: Array.from(selectedReports),
        target_rule_version: currentRuleVersion,
        preview_only: previewOnly
      });

      const operationId = response.data.operation_id;
      
      // Start polling for operation status
      startPollingOperation(operationId);
      
      // Clear selections
      setSelectedReports(new Set());
      
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to start rule upgrade');
    }
  };

  const startPollingOperation = (operationId: number) => {
    // Initial fetch
    fetchOperationStatus(operationId);
    
    // Set up polling interval
    const interval = setInterval(() => {
      fetchOperationStatus(operationId);
    }, 2000); // Poll every 2 seconds
    
    setPollingInterval(interval);
  };

  const fetchOperationStatus = async (operationId: number) => {
    try {
      const response = await apiService.get(`/v2/admin/bulk-operations/${operationId}/status`);
      const operation = response.data.data;
      
      setActiveOperation(operation);
      
      // Stop polling if operation is complete
      if (operation.status === 'completed' || operation.status === 'failed' || operation.status === 'cancelled') {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        
        // Refresh data
        fetchEligibleReports();
        fetchOperationHistory();
      }
    } catch (err) {
      console.error('Failed to fetch operation status:', err);
    }
  };

  const cancelOperation = async () => {
    if (!activeOperation || !activeOperation.can_cancel) return;
    
    try {
      await apiService.post(`/v2/admin/bulk-operations/${activeOperation.id}/cancel`);
      
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      
      setActiveOperation(null);
      fetchOperationHistory();
      
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to cancel operation');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <ArrowUpIcon className="h-5 w-5 text-blue-500 animate-pulse" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">Rule Upgrade Manager</h2>
          <p className="mt-1 text-sm text-gray-600">
            Upgrade reports to the latest business rules (version {currentRuleVersion})
          </p>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Active Operation */}
        {activeOperation && (
          <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-blue-900">
                {previewOnly ? 'Preview in Progress' : 'Upgrade in Progress'}
              </h3>
              {activeOperation.can_cancel && (
                <button
                  onClick={cancelOperation}
                  className="text-sm text-blue-700 hover:text-blue-900 underline"
                >
                  Cancel
                </button>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Progress</span>
                <span className="font-medium">{activeOperation.progress.percentage}%</span>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${activeOperation.progress.percentage}%` }}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                <div>
                  <span className="text-gray-600">Processed:</span>
                  <span className="ml-1 font-medium">
                    {activeOperation.progress.processed_items} / {activeOperation.progress.total_items}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Successful:</span>
                  <span className="ml-1 font-medium text-green-600">
                    {activeOperation.progress.successful_items}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Failed:</span>
                  <span className="ml-1 font-medium text-red-600">
                    {activeOperation.progress.failed_items}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Report Selection */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Eligible Reports ({eligibleReports.length})
            </h3>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={previewOnly}
                  onChange={(e) => setPreviewOnly(e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Preview Only</span>
              </label>
              
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {selectedReports.size === eligibleReports.filter(r => r.compatible).length
                  ? 'Deselect All'
                  : 'Select All Compatible'}
              </button>
            </div>
          </div>

          {eligibleReports.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ArrowUpIcon className="h-12 w-12 mx-auto mb-4" />
              <p>All reports are up to date with the latest rules!</p>
            </div>
          ) : (
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      <input
                        type="checkbox"
                        checked={selectedReports.size === eligibleReports.filter(r => r.compatible).length}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300"
                      />
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Report Name
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Type
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Current Version
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Compatibility
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {eligibleReports.map((report) => (
                    <tr key={report.id} className={!report.compatible ? 'bg-gray-50' : ''}>
                      <td className="whitespace-nowrap px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedReports.has(report.id)}
                          onChange={() => handleSelectReport(report.id)}
                          disabled={!report.compatible}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300 disabled:opacity-50"
                        />
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {report.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <span className="capitalize">{report.type}</span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {report.current_version} → {report.target_version}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {report.compatible ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Compatible
                          </span>
                        ) : (
                          <div className="flex items-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Incompatible
                            </span>
                            <ExclamationTriangleIcon
                              className="h-4 w-4 ml-1 text-red-500"
                              title={report.compatibility_message}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedReports.size > 0 && (
            <div className="mt-6 flex justify-end">
              <button
                onClick={startRuleUpgrade}
                disabled={!!activeOperation}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowUpIcon className="h-4 w-4 mr-2" />
                {previewOnly ? 'Preview Upgrade' : 'Start Upgrade'} ({selectedReports.size} reports)
              </button>
            </div>
          )}
        </div>

        {/* Operation History */}
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showHistory ? 'Hide' : 'Show'} Operation History
          </button>
          
          {showHistory && operationHistory.length > 0 && (
            <div className="mt-4 space-y-2">
              {operationHistory.slice(0, 5).map((op) => (
                <div
                  key={op.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(op.status)}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Operation #{op.id}
                      </p>
                      <p className="text-xs text-gray-500">
                        {op.progress.total_items} reports • {op.started_at}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-900">
                      {op.progress.successful_items} successful
                    </p>
                    {op.progress.failed_items > 0 && (
                      <p className="text-xs text-red-600">
                        {op.progress.failed_items} failed
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RuleUpgradeManager;